import React from 'react';
import './VideoList.css';

const VideoList = ({ videos, onVideoSelect, onDeleteVideo, selectedVideoId }) => {
  // Function to format the duration in MM:SS format
  const formatDuration = (durationInSeconds) => {
    if (!durationInSeconds) return '00:00';
    
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Function to format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Function to get status label and class
  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'processing':
        return { label: 'Processing', className: 'status-processing' };
      case 'complete':
        return { label: 'Ready', className: 'status-ready' };
      case 'error':
        return { label: 'Error', className: 'status-error' };
      case 'uploaded':
        return { label: 'Uploaded', className: 'status-uploaded' };
      default:
        return { label: status, className: 'status-default' };
    }
  };

  // Sort videos by creation date (newest first)
  const sortedVideos = [...videos].sort((a, b) => {
    return new Date(b.createdAt || b.uploadDate) - new Date(a.createdAt || a.uploadDate);
  });

  return (
    <div className="video-list">
      {sortedVideos.length === 0 ? (
        <p className="no-videos-message">No videos found. Upload your first video!</p>
      ) : (
        <ul>
          {sortedVideos.map((video) => {
            const statusInfo = getStatusInfo(video.status);
            const isSelected = video.videoId === selectedVideoId;
            
            // Use title if available, otherwise use filename without extension
            const displayTitle = video.title || 
              (video.fileName ? video.fileName.substring(0, video.fileName.lastIndexOf('.')) : 'Untitled Video');
            
            return (
              <li 
                key={video.videoId} 
                className={`video-item ${isSelected ? 'selected' : ''}`}
                onClick={() => video.status?.toLowerCase() === 'complete' && onVideoSelect(video)}
              >
                <div className="video-item-content">
                  <div className="video-thumbnail">
                    {video.thumbnailUrl ? (
                      <img src={video.thumbnailUrl} alt={displayTitle} />
                    ) : (
                      <div className="thumbnail-placeholder">
                        {video.status?.toLowerCase() === 'complete' ? 'No Thumbnail' : 'Processing...'}
                      </div>
                    )}
                    {video.duration && (
                      <span className="video-duration">{formatDuration(video.duration)}</span>
                    )}
                  </div>
                  
                  <div className="video-details">
                    <h3 className="video-title">{displayTitle}</h3>
                    <p className="video-date">{formatDate(video.createdAt || video.uploadDate)}</p>
                    {video.uploadedBy && (
                      <p className="video-uploader">Uploaded by: {video.uploadedBy}</p>
                    )}
                    <div className={`video-status ${statusInfo.className}`}>
                      {statusInfo.label}
                    </div>
                  </div>
                  
                  <div className="video-actions">
                    {video.status?.toLowerCase() === 'complete' && (
                      <button 
                        className="play-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onVideoSelect(video);
                        }}
                      >
                        Play
                      </button>
                    )}
                    <button 
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this video?')) {
                          onDeleteVideo(video.videoId);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                {video.description && (
                  <p className="video-description">{video.description}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default VideoList;
